import {css} from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {ERegularText, ESmallText} from 'webapp-lib/Components';
import {classy, secondsToTimeDurationNumber} from 'webapp-lib/utils';

import {TimeEntriesPerDayDurations} from '@lexian/api/timetracker-api';
import {formattedHoursWithUnit} from '_/utilities/temporal';

export function Summary({
  durations,
}: {
  durations: TimeEntriesPerDayDurations | undefined;
}): React.ReactElement {
  const {t} = useTranslation('timetracker');

  const assignedHours = toHours(durations?.assigned);
  const billableHours = toHours(durations?.billable);
  const nonBillableHours = toHours(durations?.notBillable);
  const unassignedHours = toHours(durations?.unassigned);
  const totalHours = toHours(durations?.total);

  return (
    <StyledSummary>
      <ERegularText>{t('components.summary.assignedHours')}</ERegularText>
      <Hours h={assignedHours} />
      <ESmallText>{t('components.summary.billableHours')}</ESmallText>
      <Hours h={billableHours} type='DETAIL' />
      <ESmallText>{t('components.summary.nonBillableHours')}</ESmallText>
      <Hours h={nonBillableHours} type='DETAIL' />
      <ERegularText>{t('components.summary.unassignedHours')}</ERegularText>
      <Hours h={unassignedHours} />
      <Total>{t('components.summary.totalHours')}</Total>
      <Hours h={totalHours} type='TOTAL' />
    </StyledSummary>
  );
}

function toHours(seconds: number | undefined): number {
  return secondsToTimeDurationNumber(seconds ?? 0);
}

function Hours({
  h,
  type,
}: {
  h: number;
  type?: 'DETAIL' | 'TOTAL';
}): React.ReactElement {
  return (
    <StyledHours
      className={classy({
        detail: type === 'DETAIL',
        total: type === 'TOTAL',
      })}
    >
      {formattedHoursWithUnit(h)}
    </StyledHours>
  );
}

const StyledSummary = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;

  padding-top: var(--gutter-medium);
  padding-right: var(--gutter-small);
  text-align: right;
  row-gap: var(--gutter-little);
  align-self: flex-end;
`;

const mixinTotal = css`
  border-top: var(--color-core-black) solid var(--regular-border-width);
  font-weight: var(--font-weight-highlight);
  padding-top: var(--gutter-little);
`;

const Total = styled.div`
  ${mixinTotal};
`;

const StyledHours = styled.span`
  padding-left: var(--gutter);
  text-align: right;

  &.detail {
    font-size: var(--font-size-tiny);
  }

  &.total {
    ${mixinTotal};
  }
`;
